var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { z } from 'zod';
import dayjs from 'dayjs';
var isDateValidFormat = function (date) { return dayjs(date, 'YYYY-MM-DDTHH:mm:ss[Z]', true).isValid(); };
var envSchema = z.object({
    VITE_API_URL: z.string().default('/api').optional(),
    VITE_PLATFORM_URL: z.string().optional(),
    VITE_PANEL_URL: z.string().optional(),
    VITE_LIBERIS_IFRAME_URL: z.string().optional(),
    VITE_PANEL_URL_FINANCING_APP_PATH: z.string().optional(),
    VITE_PRAGMA_FAQ_URL: z.string().optional(),
    VITE_PRAGMA_PROCESS_URL: z.string().optional(),
    VITE_PRAGMA_PROCESS_REDIRECT: z.string().optional(),
    VITE_PRAGMA_PROCESS_IFRAME_URL: z.string().optional(),
    VITE_POLLING_INTERVAL: z
        .string()
        .refine(function (value) {
        var parsedNumber = Number(value);
        return !isNaN(parsedNumber);
    })
        .optional(),
    VITE_POLLING_TIMEOUT: z
        .string()
        .refine(function (value) {
        var parsedNumber = Number(value);
        return !isNaN(parsedNumber);
    })
        .optional(),
    VITE_PRAGMA_PROMOTION_ACTIVE: z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return Number.isInteger(val) && val >= 0 && val <= 1; }, {
        error: 'VITE_PRAGMA_PROMOTION_ACTIVE must be an integer between 0 and 1',
    })
        .optional(),
    VITE_PRAGMA_PROMOTION_TENOR: z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return Number.isInteger(val) && val >= 0 && val <= 30; }, {
        error: 'PROMOTION_TENOR must be an integer between 0 and 30',
    })
        .optional(),
    VITE_PRAGMA_PROMOTION_TENOR_DEFAULT_CHOOSE: z.string().optional(),
    VITE_PRAGMA_PROMOTION_VARIANT: z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return Number.isInteger(val) && val >= 1 && val <= 5; }, {
        error: 'PRAGMA_PROMOTION_VARIANT must be an integer between 1 and 5',
    })
        .optional(),
    VITE_PRAGMA_PROMOTION_START_DATE: z
        .string()
        .refine(function (val) {
        return isDateValidFormat(val);
    }, {
        error: 'PRAGMA_PROMOTION_START_DATE must be in format YYYY-MM-DDTHH:MM:SSZ',
    })
        .optional(),
    VITE_PRAGMA_PROMOTION_END_DATE: z
        .string()
        .refine(function (val) {
        return isDateValidFormat(val);
    }, {
        error: 'PRAGMA_PROMOTION_END_DATE must be in format YYYY-MM-DDTHH:MM:SSZ',
    })
        .optional(),
    VITE_PRAGMA_DISCOUNT_LOCAL_STORAGE_TTL: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return !isNaN(val) && val > 0; }, {
        error: 'VITE_PRAGMA_DISCOUNT_LOCAL_STORAGE_TTL must be a positive number (milliseconds)',
    })
        .optional()),
    VITE_WIDGET_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1 || val === 2; }, {
        error: 'VITE_WIDGET_VARIANT must be 1 or 2',
    })
        .optional()),
    VITE_WIDGET_TOPUP_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1; }, {
        error: 'VITE_WIDGET_TOPUP_VARIANT must be 1 when enabled',
    })
        .optional()),
    VITE_WIDGET_FINISH_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1; }, {
        error: 'VITE_WIDGET_FINISH_VARIANT must be 1 when enabled',
    })
        .optional()),
    VITE_WIDGET_ERROR_ZERO_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1; }, {
        error: 'VITE_WIDGET_ERROR_ZERO_VARIANT must be 1 when enabled',
    })
        .optional()),
    VITE_WIDGET_ERROR_PERMISSION_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1; }, {
        error: 'VITE_WIDGET_ERROR_PERMISSION_VARIANT must be 1 when enabled',
    })
        .optional()),
    VITE_WIDGET_TOPUP_TOO_LOW_VARIANT: z.preprocess(function (val) { return (val === '' ? undefined : val); }, z
        .union([z.string(), z.number()])
        .transform(function (val) { return Number(val); })
        .refine(function (val) { return val === 1; }, {
        error: 'VITE_WIDGET_TOPUP_TOO_LOW_VARIANT must be 1 when enabled',
    })
        .optional()),
    VITE_TESTMODE_STANDALONE: z.string().optional(),
    VITE_MOCKED_API: z.string().optional(),
});
export var initialEnv = envSchema.parse(import.meta.env);
export var env = initialEnv;
export function updateEnv(newEnv) {
    env = envSchema.parse(__assign(__assign({}, env), newEnv));
}
